import React, { useEffect, useState, useRef, ChangeEvent, useCallback } from 'react';
import { Row, Container, Col, InputGroup, Button, Form } from 'react-bootstrap';
import { Stars } from 'react-bootstrap-icons';
import { parseEther, formatEther } from 'viem';
import { useContractWrite, usePrepareContractWrite, useWaitForTransaction, useBalance } from 'wagmi';
import { BitstraysTokenABI } from '@bitstrays/contracts';

import classes from './Hero.module.css';
import { StandaloneCandidateBitstrayWithSeed } from '../StandaloneBitstray';
import { LoadingBitstray } from '../Bitstray';
import { TotalSupply } from '../TotalSupply';
import { IBitstraySeed } from '../../wrappers/bitstrayToken';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { AlertModal, setAlertModal, setStateBackgroundColor } from '../../state/slices/application';
import config from '../../config';
import { getProof } from '../../utils/whitelist';

import { beige, grey, green, blue, creme, pink, purple } from '../../utils/bitstraysBgColors';

import Ethereum from '../../assets/icons/ethereum-eth-logo.svg';

interface PublicMintProps {
  totalSupply: number;
  mintPrice: bigint;
}

const PublicMint: React.FC<PublicMintProps> = props => {
  const activeAccount = useAppSelector(state => state.account.activeAccount);
  const isSale = useAppSelector(state => state.sales.isSaleActive);
  const isPreSale = useAppSelector(state => state.sales.isPreSaleActive);
  const { totalSupply, mintPrice } = props;

  const [mintInput, setMintInput] = useState('0.05');
  const mintInputRef = useRef<HTMLInputElement>(null);

  const [mintQtyInput, setMintQtyInput] = useState('1');
  const mintQtyInputRef = useRef<HTMLSelectElement>(null);

  const bitstrayCandiateId = BigInt(totalSupply);

  const dispatch = useAppDispatch();

  // Get proof for whitelist
  const proof = getProof(activeAccount) as `0x${string}`[];

  // Prepare the mint transaction
  const { config: mintConfig, error: prepareError } = usePrepareContractWrite({
    address: config.addresses.bitstraysToken as `0x${string}`,
    abi: BitstraysTokenABI,
    functionName: 'publicMint',
    args: [BigInt(mintQtyInput), proof],
    value: parseEther(mintInput),
    enabled: !!activeAccount && parseFloat(mintInput) > 0,
  });

  const { write: mint, data: mintData, isLoading: isMinting, error: mintError } = useContractWrite(mintConfig);

  const { isLoading: isConfirming, isSuccess } = useWaitForTransaction({
    hash: mintData?.hash,
  });

  // Get user's balance
  const { data: balanceData } = useBalance({
    address: activeAccount as `0x${string}`,
    enabled: !!activeAccount,
  });

  const setModal = useCallback((modal: AlertModal) => dispatch(setAlertModal(modal)), [dispatch]);

  // Format error message with helpful context
  const formatErrorMessage = useCallback((error: Error): string => {
    const rawMessage = error.message || 'Transaction failed';

    // Check for insufficient balance error
    if (rawMessage.includes('exceeds the balance')) {
      const mintCost = mintPrice * BigInt(mintQtyInput);
      const mintCostEth = parseFloat(formatEther(mintCost)).toFixed(4);
      const currentBalance = balanceData?.value ?? 0n;
      const currentBalanceEth = parseFloat(formatEther(currentBalance)).toFixed(4);

      return `Insufficient balance. You need ${mintCostEth} ETH to mint ${mintQtyInput} Bitstray${parseInt(mintQtyInput) > 1 ? 's' : ''}, but you only have ${currentBalanceEth} ETH.`;
    }

    // Check for user rejection
    if (rawMessage.includes('User rejected') || rawMessage.includes('user rejected')) {
      return 'Transaction was cancelled.';
    }

    // Return first line of other errors
    return rawMessage.split('\n')[0];
  }, [mintPrice, mintQtyInput, balanceData]);

  // Handle mint errors
  useEffect(() => {
    if (mintError) {
      setModal({
        title: 'Mint Error',
        message: formatErrorMessage(mintError),
        show: true,
      });
    }
  }, [mintError, setModal, formatErrorMessage]);

  // Handle successful mint
  useEffect(() => {
    if (isSuccess) {
      setModal({
        title: 'Success!',
        message: 'Your Bitstray has been minted!',
        show: true,
      });
    }
  }, [isSuccess, setModal]);

  const loadedBitstrayHandler = (seed: IBitstraySeed) => {
    let color = grey;
    switch (seed.background) {
      case 0:
        color = grey;
        break;
      case 1:
        color = beige;
        break;
      case 2:
        color = green;
        break;
      case 3:
        color = blue;
        break;
      case 4:
        color = creme;
        break;
      case 5:
        color = pink;
        break;
      case 6:
        color = purple;
        break;
      default:
        color = grey;
    }
    dispatch(setStateBackgroundColor(color));
  };

  const publicMintContent = (
    <div className={classes.publicMintWrapper}>
      <TotalSupply totalSupply={totalSupply} maxSupply={10000} />
    </div>
  );

  const mintPriceEth = (price: bigint): string => {
    return formatEther(price);
  };

  const mintInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // disable more than 2 digits after decimal point
    if (input.includes('.') && event.target.value.split('.')[1].length > 2) {
      return;
    }

    setMintInput(event.target.value);
  };

  const mintQtyInputHandler = (event: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const input = BigInt(event.target.value);
    setMintQtyInput(event.target.value);
    setMintInput(formatEther(mintPrice * input));
  };

  const placeMintHandler = () => {
    if (!mint) {
      // Show prepare error if mint isn't ready
      if (prepareError) {
        setModal({
          title: 'Mint Error',
          message: formatErrorMessage(prepareError),
          show: true,
        });
      }
      return;
    }
    mint();
  };

  useEffect(() => {
    if (mintPrice > 0n) {
      setMintInput(mintPriceEth(mintPrice));
    }
  }, [mintPrice]);

  const bitstrayContent = (
    <div className={classes.bitstrayWrapper}>
      <StandaloneCandidateBitstrayWithSeed
        bitstrayId={bitstrayCandiateId}
        onLoadSeed={loadedBitstrayHandler}
      />
    </div>
  );

  const optionPreSale = (
    <>
      <option>1</option>
      <option>2</option>
      <option>3</option>
    </>
  );

  const optionSale = (
    <>
      <option>1</option>
      <option>2</option>
      <option>3</option>
      <option>4</option>
      <option>5</option>
      <option>6</option>
      <option>7</option>
      <option>8</option>
      <option>9</option>
      <option>10</option>
    </>
  );

  const mintQty = (preSales: boolean, sales: boolean) => {
    if (preSales && !sales) {
      return optionPreSale;
    }
    return optionSale;
  };

  const isDisabled = !activeAccount || isMinting || isConfirming;
  const buttonText = isMinting ? 'Minting...' : isConfirming ? 'Confirming...' : 'Mint';

  const loadingBitstray = (
    <div className={classes.bitstrayWrapper}>
      <LoadingBitstray />
    </div>
  );

  const EthereumImg = (
    <img
      src={Ethereum}
      width="20"
      height="20"
      className="d-inline-block align-middle"
      alt="Ethereum logo"
    />
  );

  const publicMint = (
    <div className={classes.publicMintCol}>
      <h1 className={classes.mintTitle}>'Adopt yours!'</h1>
      <br />
      <br />
      {publicMintContent}
      <Row>
        <Col className={classes.mintFormLabel}>Mint price:</Col>
        <Col className={classes.mintPrice}>
          {mintPriceEth(mintPrice)} {EthereumImg} <span className={classes.mintEth}>ETH</span>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col lg={{ span: 6 }}>
          <Form.Label className={classes.mintFormLabel}>How Many?</Form.Label>
        </Col>
        <Col lg={{ span: 6 }}>
          <Form.Control
            as="select"
            onChange={mintQtyInputHandler}
            ref={mintQtyInputRef}
            value={mintQtyInput}
            size="lg"
          >
            {mintQty(isPreSale, isSale)}
          </Form.Control>
        </Col>
      </Row>

      <Row>
        <Col lg={{ span: 6 }}>
          <Form.Label className={classes.mintFormLabel}>Total</Form.Label>
        </Col>
        <Col lg={{ span: 6 }}>
          <InputGroup>
            <Form.Control
              className={classes.bidInput}
              type="number"
              min="0"
              onChange={mintInputHandler}
              ref={mintInputRef}
              value={mintInput}
              readOnly
              size="lg"
            />
            <span className={classes.customPlaceholder}>ETH</span>
          </InputGroup>
        </Col>
      </Row>
      <Row>
        <Col lg={6}></Col>
        <Col lg={6}>
          <Button
            onClick={placeMintHandler}
            type="button"
            variant="danger"
            size="lg"
            className={`${classes.btn3d} ${classes.btnDanger}`}
            disabled={isDisabled}
          >
            <Stars /> {buttonText}
          </Button>
        </Col>
      </Row>
    </div>
  );

  const stateBgColor = useAppSelector(state => state.application.stateBackgroundColor);
  return (
    <div style={{ backgroundColor: stateBgColor }}>
      <Container fluid="lg">
        <Row>
          <div className="snow"></div>
          <Col lg={{ span: 6 }} className={classes.bitstrayContentCol}>
            {bitstrayContent ? bitstrayContent : loadingBitstray}
          </Col>
          <Col lg={{ span: 6 }}>{publicMint}</Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicMint;
